import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import "./LeadPopup.css";
import {
  getCategories,
  getSubCategories,
  saveLead,
  getPopupSettings,
} from "../../data/modules/dynamic-module";
import { useAuth } from "../../hooks/AuthProvider";

const PHONE_COUNTRIES = [
  { code: "IN", dial: "+91", flag: "🇮🇳", min: 10, max: 10 },
  { code: "US", dial: "+1", flag: "🇺🇸", min: 10, max: 10 },
  { code: "GB", dial: "+44", flag: "🇬🇧", min: 10, max: 10 },
  { code: "AE", dial: "+971", flag: "🇦🇪", min: 9, max: 9 },
  { code: "SA", dial: "+966", flag: "🇸🇦", min: 9, max: 9 },
  { code: "QA", dial: "+974", flag: "🇶🇦", min: 8, max: 8 },
  { code: "OM", dial: "+968", flag: "🇴🇲", min: 8, max: 8 },
  { code: "AU", dial: "+61", flag: "🇦🇺", min: 9, max: 9 },
  { code: "SG", dial: "+65", flag: "🇸🇬", min: 8, max: 8 },
  { code: "NZ", dial: "+64", flag: "🇳🇿", min: 8, max: 10 },
];

const CLASS_OPTIONS = [
  "Pre-School / Kindergarten",
  "Class 1-5 (Primary)",
  "Class 6-8 (Middle)",
  "Class 9-10 (Secondary)",
  "Class 11-12 (Senior Secondary)",
  "College / University",
  "Working Professional",
  "Adult Learner / Hobby",
  "Other",
];

// Fallback defaults — used until settings load, or if the fetch fails.
const DEFAULTS = {
  enabled: true,
  loggedOutOnly: true,
  desktopExitIntent: true,
  desktopBackButton: false,
  desktopTimerEnabled: false,
  desktopTimerSeconds: 45,
  mobileTimerSeconds: 25,
  mobileScrollUp: false,
  minTimeOnPageSeconds: 5,
  frequencyMode: "session",
  frequencyDays: 7,
  reshowAfterSubmit: false,
  fields: {
    name: { show: true, required: true },
    email: { show: true, required: false },
    phone: { show: true, required: false },
    subject: { show: true, required: false },
    studentClass: { show: true, required: false },
  },
  softCapture: true,
  softCaptureThreshold: "either",
  title: "Couldn't find the right tutor?",
  subtitle: "Tell us what you need and we'll match you with the perfect one.",
  buttonText: "Find me a tutor",
  consentText:
    "By entering your details, you agree we may contact you to help find a tutor.",
  successMessage:
    "Our team will reach out shortly to help you find the right tutor.",
  accentColor: "#ed5a09",
  skipPaths: ["/tutor-details", "/authentication", "/dashboard", "/video-call"],
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function phoneRule(dial) {
  return PHONE_COUNTRIES.find((c) => c.dial === dial) || { min: 6, max: 15 };
}
function phoneOk(dial, number) {
  const r = phoneRule(dial);
  const n = (number || "").replace(/\D/g, "").length;
  return n >= r.min && n <= r.max;
}
function getLeadKey() {
  try {
    let k = localStorage.getItem("xz_lead_key");
    if (!k) {
      k = "web-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      localStorage.setItem("xz_lead_key", k);
    }
    return k;
  } catch {
    return "web-" + Date.now().toString(36);
  }
}

export default function LeadPopup() {
  const location = useLocation();
  const { isLoggedIn } = useAuth();

  const [cfg, setCfg] = useState(DEFAULTS);
  const [cfgLoaded, setCfgLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phoneDial: "+91",
    phoneNumber: "",
    category: "",
    subject: "",
    studentClass: "",
  });

  const softSavedRef = useRef(false);
  const mountTimeRef = useRef(Date.now());

  // Load settings once (merge onto defaults; keep defaults on failure).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await getPopupSettings();
        if (alive && s && typeof s === "object") {
          setCfg({
            ...DEFAULTS,
            ...s,
            fields: { ...DEFAULTS.fields, ...(s.fields || {}) },
            skipPaths:
              Array.isArray(s.skipPaths) && s.skipPaths.length
                ? s.skipPaths
                : DEFAULTS.skipPaths,
          });
        }
      } catch {
        // keep defaults
      } finally {
        if (alive) setCfgLoaded(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const isTutorSearchPage = location.pathname.split("/").length === 4;
  const skip =
    !cfg.enabled ||
    (cfg.loggedOutOnly && isLoggedIn) ||
    isTutorSearchPage ||
    (cfg.skipPaths || []).some((p) => p && location.pathname.startsWith(p));

  const alreadyHandled = () => {
    try {
      if (!cfg.reshowAfterSubmit && localStorage.getItem("xz_lead_done") === "1")
        return true;
      if (cfg.frequencyMode === "session") {
        if (sessionStorage.getItem("xz_lead_seen") === "1") return true;
      } else if (cfg.frequencyMode === "days") {
        const last = parseInt(localStorage.getItem("xz_lead_last") || "0", 10);
        if (last && Date.now() - last < cfg.frequencyDays * 86400000) return true;
      }
      // "always" → no seen-gate
    } catch {
      // ignore
    }
    return false;
  };

  const markSeen = () => {
    try {
      if (cfg.frequencyMode === "session") {
        sessionStorage.setItem("xz_lead_seen", "1");
      } else if (cfg.frequencyMode === "days") {
        localStorage.setItem("xz_lead_last", String(Date.now()));
      }
    } catch {
      // ignore
    }
  };

  // Triggers — set up only once settings are loaded and the page is eligible.
  useEffect(() => {
    if (!cfgLoaded || skip || alreadyHandled()) return;

    let fired = false;
    const minMs = (cfg.minTimeOnPageSeconds || 0) * 1000;

    const trigger = () => {
      if (fired || alreadyHandled()) return;
      if (Date.now() - mountTimeRef.current < minMs) return;
      fired = true;
      markSeen();
      setOpen(true);
    };

    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) trigger();
    };

    const isTouch =
      typeof window !== "undefined" &&
      ("ontouchstart" in window || navigator.maxTouchPoints > 0);

    const timers = [];
    const cleanups = [];

    if (isTouch) {
      timers.push(
        window.setTimeout(trigger, Math.max(minMs, (cfg.mobileTimerSeconds || 25) * 1000)),
      );
      if (cfg.mobileScrollUp) {
        let lastY = window.scrollY;
        let downMax = 0;
        const onScroll = () => {
          const y = window.scrollY;
          if (y > lastY) downMax = Math.max(downMax, y);
          if (downMax > 300 && lastY - y > 120) trigger();
          lastY = y;
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        cleanups.push(() => window.removeEventListener("scroll", onScroll));
      }
    } else {
      if (cfg.desktopExitIntent) {
        document.addEventListener("mouseout", onMouseOut);
        cleanups.push(() => document.removeEventListener("mouseout", onMouseOut));
      }
      if (cfg.desktopTimerEnabled) {
        timers.push(
          window.setTimeout(
            trigger,
            Math.max(minMs, (cfg.desktopTimerSeconds || 45) * 1000),
          ),
        );
      }
      if (cfg.desktopBackButton) {
        try {
          window.history.pushState({ xzLead: 1 }, "");
        } catch {
          // ignore
        }
        let trapped = false;
        const onPop = () => {
          if (trapped) return;
          trapped = true;
          trigger();
          try {
            window.history.pushState({ xzLead: 1 }, "");
          } catch {
            // ignore
          }
        };
        window.addEventListener("popstate", onPop);
        cleanups.push(() => window.removeEventListener("popstate", onPop));
      }
    }

    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      cleanups.forEach((fn) => fn());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfgLoaded, skip, location.pathname]);

  // Load categories when first opened (only if subject field shown).
  useEffect(() => {
    if (!open || categories.length || !cfg.fields?.subject?.show) return;
    (async () => {
      const cats = await getCategories();
      setCategories(Array.isArray(cats) ? cats : []);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onCategory = async (value) => {
    setForm((p) => ({ ...p, category: value, subject: "" }));
    setSubjects([]);
    if (value) {
      const subs = await getSubCategories({ category: value });
      setSubjects(Array.isArray(subs) ? subs : []);
    }
  };

  const buildPhone = (f) =>
    `${f.phoneDial}${(f.phoneNumber || "").replace(/\D/g, "")}`;

  const softThresholdMet = (next) => {
    const hasEmail = EMAIL_RE.test(next.email.trim());
    const hasPhone = phoneOk(next.phoneDial, next.phoneNumber);
    if (cfg.softCaptureThreshold === "email") return hasEmail;
    if (cfg.softCaptureThreshold === "phone") return hasPhone;
    return hasEmail || hasPhone;
  };

  const maybeSoftSave = (next) => {
    if (!cfg.softCapture || softSavedRef.current) return;
    if (!softThresholdMet(next)) return;
    const hasPhone = phoneOk(next.phoneDial, next.phoneNumber);
    softSavedRef.current = true;
    saveLead({
      leadKey: getLeadKey(),
      name: next.name.trim(),
      email: next.email.trim(),
      phone: hasPhone ? buildPhone(next) : "",
      category: next.category,
      subject: next.subject,
      studentClass: next.studentClass,
      isPartial: true,
      pageUrl: location.pathname,
    });
  };

  const update = (patch) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      maybeSoftSave(next);
      return next;
    });
  };

  const close = () => {
    markSeen();
    setOpen(false);
  };

  const missingRequired = () => {
    const f = cfg.fields || {};
    if (f.name?.show && f.name?.required && !form.name.trim()) return true;
    if (f.email?.show && f.email?.required && !EMAIL_RE.test(form.email.trim()))
      return true;
    if (f.phone?.show && f.phone?.required && !phoneOk(form.phoneDial, form.phoneNumber))
      return true;
    if (f.subject?.show && f.subject?.required && !form.subject) return true;
    if (f.studentClass?.show && f.studentClass?.required && !form.studentClass)
      return true;
    // always need at least one contact point to be useful
    const hasEmail = EMAIL_RE.test(form.email.trim());
    const hasPhone = phoneOk(form.phoneDial, form.phoneNumber);
    if (!hasEmail && !hasPhone) return true;
    return false;
  };

  const submit = async () => {
    if (missingRequired()) return;
    const hasPhone = phoneOk(form.phoneDial, form.phoneNumber);
    setSubmitting(true);
    await saveLead({
      leadKey: getLeadKey(),
      name: form.name.trim(),
      email: form.email.trim(),
      phone: hasPhone ? buildPhone(form) : "",
      category: form.category,
      subject: form.subject,
      studentClass: form.studentClass,
      isPartial: false,
      pageUrl: location.pathname,
    });
    try {
      localStorage.setItem("xz_lead_done", "1");
    } catch {
      // ignore
    }
    setSubmitting(false);
    setDone(true);
  };

  if (!open) return null;

  const accent = cfg.accentColor || "#ed5a09";
  const f = cfg.fields || {};

  return (
    <div className="lead-pop-overlay" onClick={close}>
      <div className="lead-pop" onClick={(e) => e.stopPropagation()}>
        <button className="lead-pop-x" onClick={close} aria-label="Close">
          ×
        </button>

        {done ? (
          <div className="lead-pop-done">
            <div className="lead-pop-tick" style={{ color: accent }}>
              ✓
            </div>
            <h3>Thank you!</h3>
            <p>{cfg.successMessage}</p>
            <button
              className="lead-pop-btn"
              style={{ background: accent }}
              onClick={close}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <h3 className="lead-pop-title">{cfg.title}</h3>
            <p className="lead-pop-sub">{cfg.subtitle}</p>

            {f.name?.show && (
              <div className="lead-pop-field">
                <input
                  type="text"
                  placeholder={"Your name" + (f.name?.required ? " *" : "")}
                  value={form.name}
                  onChange={(e) => update({ name: e.target.value })}
                />
              </div>
            )}

            {f.email?.show && (
              <div className="lead-pop-field">
                <input
                  type="email"
                  placeholder={"Email address" + (f.email?.required ? " *" : "")}
                  value={form.email}
                  onChange={(e) => update({ email: e.target.value })}
                />
              </div>
            )}

            {f.phone?.show && (
              <div className="lead-pop-field lead-pop-phone">
                <select
                  value={form.phoneDial}
                  onChange={(e) =>
                    update({
                      phoneDial: e.target.value,
                      phoneNumber: form.phoneNumber.slice(
                        0,
                        phoneRule(e.target.value).max,
                      ),
                    })
                  }
                >
                  {PHONE_COUNTRIES.map((c) => (
                    <option key={c.code} value={c.dial}>
                      {c.flag} {c.dial}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  inputMode="numeric"
                  placeholder={"Phone number" + (f.phone?.required ? " *" : "")}
                  value={form.phoneNumber}
                  onChange={(e) =>
                    update({
                      phoneNumber: e.target.value
                        .replace(/\D/g, "")
                        .slice(0, phoneRule(form.phoneDial).max),
                    })
                  }
                />
              </div>
            )}

            {f.subject?.show && (
              <div className="lead-pop-row">
                <select
                  value={form.category}
                  onChange={(e) => onCategory(e.target.value)}
                >
                  <option value="">Category</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <select
                  value={form.subject}
                  onChange={(e) => update({ subject: e.target.value })}
                  disabled={!form.category}
                >
                  <option value="">Subject</option>
                  {subjects.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {f.studentClass?.show && (
              <div className="lead-pop-field">
                <select
                  value={form.studentClass}
                  onChange={(e) => update({ studentClass: e.target.value })}
                >
                  <option value="">Select class / level</option>
                  {CLASS_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <button
              className="lead-pop-btn"
              style={{ background: accent }}
              onClick={submit}
              disabled={submitting}
            >
              {submitting ? "Sending…" : cfg.buttonText}
            </button>

            <p className="lead-pop-consent">{cfg.consentText}</p>
          </>
        )}
      </div>
    </div>
  );
}
